/**
 * @Author: wangrenjie86@gmail.com
 * @Date: 2024-09-18 11:02:17
 * @LastEditors: wangrenjie86@gmail.com
 * @LastEditTime: 2026-01-09 14:02:19
 * @FilePath: /src/directives/sticky-table.js
 * @Description: 修复切换tab吸顶失效+内存泄漏+全局变量污染+this指向报错
 */
'use strict';
function throttle(func, limit) {
    let inThrottle;
    return function () {
        const args = arguments;
        const context = this;
        if (!inThrottle) {
            func.apply(context, args);
            inThrottle = true;
            setTimeout(() => (inThrottle = false), limit);
        }
    };
}

// 滚动的容器选择器
const tableScrollSelector = '.ant-table-content';
// 表头选择器
const tableHeaderSelector = '.ant-table-thead';

/**
 * 获取配置信息
 * @param {*} binding 
 * @returns 
 */
const getBindingConfig = binding => {
    const params = binding.value || {};
    const fixedTop = params.fixedTop || 0;
    const zIndex = params.zIndex || 1000;
    const bgColor = params.bgColor || '#fff';
    const disabled = params.disabled !== undefined ? !!params.disabled : (binding.value ? false : true);
    const { scrollContainerId = 'stickyBox' } = params;
    return {
        fixedTop,
        zIndex,
        scrollContainerId,
        bgColor,
        disabled,
    };
};

// 抽离独立的工具方法，完全避免this指向问题
const resizeStickyHeader = throttle((state) => {
    state.stickyBoxEle.style.width = `${state.tableScrollEle.offsetWidth}px`;
});
const setScrollX = throttle((state) => {
    state.stickyBoxEle.scrollLeft = state.tableScrollEle.scrollLeft;
});
const setBodyScrollX = throttle((state) => {
    state.tableScrollEle.scrollLeft = state.stickyBoxEle.scrollLeft;
});
const adaptStickyHeader = (state) => {
    resizeStickyHeader(state);
    setScrollX(state);
};

// 【核心】独立封装 绑定事件方法
const doWatch = (el) => {
    const state = el._stickyState;
    if (!state || !state.container) return;
    state.container.addEventListener('scroll', state.listenAction);
    if (state.isWindow) {
        state.container.addEventListener('resize', () => resizeStickyHeader(state));
    } else {
        state.resizeObserver = new ResizeObserver(() => {
            resizeStickyHeader(state);
        });
        state.resizeObserver.observe(state.container);
    }
    state.tableScrollEle?.addEventListener('scroll', () => setScrollX(state));
    state.stickyBoxEle?.addEventListener('scroll', () => setBodyScrollX(state));
};

// 【核心】独立封装 解绑事件方法
const doUnwatch = (el) => {
    const state = el._stickyState;
    if (!state) return;
    // 解绑所有滚动事件
    state.container?.removeEventListener('scroll', state.listenAction);
    // 解绑窗口缩放/销毁ResizeObserver
    if (state.isWindow) {
        state.container?.removeEventListener('resize', () => resizeStickyHeader(state));
    } else {
        state.resizeObserver?.unobserve(state.container);
        state.resizeObserver?.disconnect(); // 彻底销毁观察者实例
    }
    // 解绑表格横向滚动事件
    state.tableScrollEle?.removeEventListener('scroll', () => setScrollX(state));
    state.stickyBoxEle?.removeEventListener('scroll', () => setBodyScrollX(state));
};

export default {
    install(app) {
        app.directive('sticky-table', {
            // 每个指令绑定的元素，都创建独立的状态对象【核心修复】，杜绝全局污染
            mounted(el, binding) {
                const bindingConfig = getBindingConfig(binding);
                const { disabled, fixedTop, zIndex, scrollContainerId, bgColor } = bindingConfig;
                if (disabled) return;
                
                // ✅ 核心：把所有变量挂载到 el 上，作为当前元素的私有状态，每个表格独立互不影响
                el._stickyState = {
                    bindingConfig,
                    container: document.getElementById(scrollContainerId) || window,
                    isWindow: false,
                    active: false,
                    hasSticky: false,
                    tableHeaderEle: null,
                    placeHeaderEle: null,
                    tableScrollEle: null,
                    stickyBoxEle: null,
                    resizeObserver: null,
                    listenAction: null,
                };
                const state = el._stickyState;
                state.isWindow = state.container === window;
                state.tableHeaderEle = el.querySelector(tableHeaderSelector);
                state.placeHeaderEle = state.tableHeaderEle.cloneNode(true);
                state.tableScrollEle = el.querySelector(tableScrollSelector);

                // 生成吸顶的容器元素
                const setStickEle = () => {
                    state.stickyBoxEle = state.tableScrollEle.cloneNode(true);
                    state.stickyBoxEle.className = `${state.stickyBoxEle.className} sticky-box`;
                    const headerEle = state.stickyBoxEle.querySelector('.ant-table-thead');
                    const bodyEle = state.stickyBoxEle.querySelector('.ant-table-tbody');
                    headerEle?.parentNode?.removeChild(headerEle);
                    bodyEle?.parentNode?.removeChild(bodyEle);
                };
                setStickEle();

                // 吸顶设置
                const sticky = () => {
                    if (state.active) return;
                    state.tableScrollEle = el.querySelector(tableScrollSelector);
                    if (!state.hasSticky) {
                        doUnwatch(el); // 调用独立方法，无this
                        setStickEle();
                        doWatch(el);  // 调用独立方法，无this
                        state.hasSticky = true;
                    }
                    state.tableHeaderEle.insertAdjacentElement('afterend', state.placeHeaderEle);
                    state.stickyBoxEle.style.cssText = `position: fixed;top: ${fixedTop}px; z-index: ${zIndex}; background-color: ${bgColor}; overflow: auto hidden; width: ${state.tableScrollEle.offsetWidth}px;`;
                    state.stickyBoxEle.querySelector('table').appendChild(state.tableHeaderEle);
                    state.tableScrollEle.insertAdjacentElement('afterend', state.stickyBoxEle);
                    setScrollX(state);
                    state.active = true;
                };

                // 解除吸顶
                const reset = () => {
                    if (!state.active) return;
                    state.placeHeaderEle.insertAdjacentElement('afterend', state.tableHeaderEle);
                    state.stickyBoxEle?.parentNode?.removeChild(state.stickyBoxEle);
                    state.placeHeaderEle?.parentNode?.removeChild(state.placeHeaderEle);
                    state.active = false;
                };

                // 滚动监听核心逻辑
                state.listenAction = throttle(() => {
                    const rectEl = state.tableScrollEle?.parentNode;
                    if (!rectEl) return reset();
                    const rect = rectEl.getBoundingClientRect();
                    const { top, bottom } = rect;
                    if (top <= fixedTop && bottom >= fixedTop) {
                        return sticky();
                    }
                    reset();
                });

                doWatch(el); // ✅ 无this，直接调用独立方法
            },

            // 更新时适配表头
            updated(el, binding) {
                const state = el._stickyState;
                if (!state) return;
                const bindingConfig = getBindingConfig(binding);
                state.bindingConfig = bindingConfig;
                if (bindingConfig.disabled) return;
                adaptStickyHeader(state);
            },

            // ✅ 核心：组件销毁时，彻底清理所有资源+删除私有状态，无内存泄漏
            unmounted(el) {
                doUnwatch(el); // ✅ 无this，直接调用独立方法
                const state = el._stickyState;
                if (state) {
                    // 解除吸顶DOM结构
                    if (state.active) {
                        state.placeHeaderEle?.insertAdjacentElement('afterend', state.tableHeaderEle);
                        state.stickyBoxEle?.parentNode?.removeChild(state.stickyBoxEle);
                        state.placeHeaderEle?.parentNode?.removeChild(state.placeHeaderEle);
                    }
                    // 销毁所有实例+清空变量
                    state.resizeObserver?.disconnect();
                    el._stickyState = null;
                    delete el._stickyState; // 删除挂载在元素上的私有状态
                }
            },
        })
    }
};